import { Link, useLocation, useParams } from "react-router-dom";
import { ChevronRight, Home } from "lucide-react";
import { services } from "@/data/services";
import { blogs } from "@/data/blogs";

/**
 * Premium Breadcrumb Navigation — SXO signal + accessibility
 * Auto-generates breadcrumb trail from current URL path.
 * Hidden on homepage. Responsive and accessible.
 */

const ROUTE_LABELS = {
  "/": "Home",
  "/about": "About",
  "/services": "Services",
  "/blogs": "Insights",
  "/resources": "Resources",
  "/contact": "Contact",
  "/privacy-policy": "Privacy Policy",
  "/terms-of-service": "Terms of Service",
};

function getBreadcrumbItems(pathname) {
  const segments = pathname.split("/").filter(Boolean);
  const items = [{ name: "Home", path: "/" }];

  if (segments.length === 0) return items;

  let currentPath = "";
  for (let i = 0; i < segments.length; i++) {
    currentPath += `/${segments[i]}`;

    if (ROUTE_LABELS[currentPath]) {
      items.push({ name: ROUTE_LABELS[currentPath], path: currentPath });
    } else if (segments[i - 1] === "services") {
      // Dynamic service slug
      const service = services.find(s => s.slug === segments[i]);
      items.push({
        name: service ? service.title : segments[i].replace(/-/g, " ").replace(/\b\w/g, l => l.toUpperCase()),
        path: currentPath,
      });
    } else if (segments[i - 1] === "blogs") {
      // Dynamic blog slug
      const blog = blogs.find(b => b.slug === segments[i]);
      items.push({
        name: blog ? blog.title : segments[i].replace(/-/g, " ").replace(/\b\w/g, l => l.toUpperCase()),
        path: currentPath,
        truncate: true,
      });
    } else {
      items.push({
        name: segments[i].replace(/-/g, " ").replace(/\b\w/g, l => l.toUpperCase()),
        path: currentPath,
      });
    }
  }

  return items;
}

export default function Breadcrumb() {
  const location = useLocation();
  const items = getBreadcrumbItems(location.pathname);

  // Don't render on homepage
  if (location.pathname === "/" || items.length <= 1) return null;

  return (
    <nav
      aria-label="Breadcrumb"
      className="relative z-10"
      data-testid="breadcrumb-nav"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <div className="py-4">
          <ol
            className="flex items-center flex-wrap gap-1 text-sm"
            itemScope
            itemType="https://schema.org/BreadcrumbList"
          >
            {items.map((item, i) => {
              const isLast = i === items.length - 1;
              return (
                <li
                  key={item.path}
                  className="flex items-center gap-1"
                  itemProp="itemListElement"
                  itemScope
                  itemType="https://schema.org/ListItem"
                >
                  {i > 0 && (
                    <ChevronRight
                      size={14}
                      className="text-white/20 flex-shrink-0"
                      aria-hidden="true"
                    />
                  )}
                  {isLast ? (
                    <span
                      className={`font-body font-medium text-[#D97706] ${
                        item.truncate ? "max-w-[200px] sm:max-w-[300px] truncate" : ""
                      }`}
                      itemProp="name"
                      aria-current="page"
                    >
                      {i === 0 ? (
                        <span className="flex items-center gap-1.5">
                          <Home size={13} />
                          {item.name}
                        </span>
                      ) : (
                        item.name
                      )}
                    </span>
                  ) : (
                    <Link
                      to={item.path}
                      className={`font-body font-medium text-white/40 hover:text-white/70 transition-colors duration-200 ${
                        item.truncate ? "max-w-[200px] sm:max-w-[300px] truncate" : ""
                      }`}
                      itemProp="item"
                    >
                      <span itemProp="name">
                        {i === 0 ? (
                          <span className="flex items-center gap-1.5">
                            <Home size={13} />
                            <span className="hidden sm:inline">{item.name}</span>
                          </span>
                        ) : (
                          item.name
                        )}
                      </span>
                    </Link>
                  )}
                  <meta itemProp="position" content={String(i + 1)} />
                </li>
              );
            })}
          </ol>
        </div>
      </div>
    </nav>
  );
}

// Export for SEOHead breadcrumb schema building
export { getBreadcrumbItems };
